import { useEffect, useState } from "react";
import { Link } from "react-router";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { SectionLabel } from "../components/shared/SectionLabel";
import * as ui from "../styles/ui";
import { api, type ConfigStatus, type GitStatus } from "../api/client";

interface IntegrationRow {
  name: string;
  configured: boolean;
  detail: string;
  hint: string;
  link?: string;
}

export function SettingsPage() {
  const [status, setStatus] = useState<ConfigStatus | null>(null);
  const [gitStatus, setGitStatus] = useState<GitStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.config.status(), api.git.status()])
      .then(([s, g]) => {
        if (!cancelled) {
          setStatus(s);
          setGitStatus(g);
        }
      })
      .catch((e) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load config");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <>
        <TopBar title="settings" />
        <div style={styles.content}>
          <p style={styles.errorText}>{error}</p>
        </div>
      </>
    );
  }

  if (!status) {
    return (
      <>
        <TopBar title="settings" />
        <div style={styles.content}>
          <Spinner size={22} label="loading…" />
        </div>
      </>
    );
  }

  const rows: IntegrationRow[] = [
    {
      name: "GitHub docs repo",
      configured: gitStatus?.connected === true,
      detail: gitStatus?.connected
        ? `${gitStatus.repo} · ${gitStatus.docCount} doc${gitStatus.docCount !== 1 ? "s" : ""} · ${gitStatus.defaultBranch}`
        : (status.githubRepo ?? "No repo configured"),
      hint: "npm run setup:local  (writes .dev.vars from gh auth)",
      link: gitStatus?.url ?? undefined,
    },
    {
      name: "Notion",
      configured: status.notion,
      detail: status.notion ? "Token configured" : "No token",
      hint: "Add NOTION_TOKEN to .dev.vars",
    },
    {
      name: "Sanity",
      configured: status.sanity,
      detail: status.sanity
        ? `${status.sanityProject ?? "?"} / ${status.sanityDataset ?? "?"}`
        : "No token",
      hint: "Add SANITY_TOKEN to .dev.vars",
    },
    {
      name: "API authentication (Clerk)",
      configured: status.clerkAuth,
      detail: status.clerkAuth
        ? "JWT verification enabled"
        : "API is unauthenticated (local dev)",
      hint: "Set CLERK_JWKS_URL in wrangler.jsonc + VITE_CLERK_PUBLISHABLE_KEY in .env",
    },
    {
      name: "PDF rendering",
      configured: status.pdfRendering,
      detail: status.pdfRendering
        ? "Real PDF via Browser Rendering (X-Shelf-Pdf-Mode: browser-rendering)"
        : "Not configured",
      hint: 'Uncomment "browser" binding in wrangler.jsonc (Workers Paid)',
    },
  ];

  return (
    <>
      <TopBar title="settings" />
      <div style={styles.content}>
        <SectionLabel>integrations</SectionLabel>
        <div style={styles.list}>
          {rows.map((row) => (
            <div key={row.name} style={styles.row}>
              <span
                style={{
                  ...styles.dot,
                  background: row.configured
                    ? "var(--comp-green)"
                    : "var(--comp-salmon)",
                }}
              />
              <div style={styles.rowBody}>
                <div style={styles.rowName}>{row.name}</div>
                <div style={styles.rowDetail}>
                  {row.link ? (
                    <a
                      href={row.link}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.repoLink}
                    >
                      {row.detail}
                    </a>
                  ) : (
                    row.detail
                  )}
                </div>
                {!row.configured && (
                  <div style={styles.rowHint}>{row.hint}</div>
                )}
                {row.name === "GitHub docs repo" &&
                  gitStatus?.error &&
                  !gitStatus.connected && (
                    <div style={styles.rowError}>{gitStatus.error}</div>
                  )}
              </div>
            </div>
          ))}
        </div>

        <div style={styles.toolsBlock}>
          <SectionLabel>tools</SectionLabel>
          <div style={styles.toolsList}>
            {TOOL_LINKS.map((tool) => (
              <Link key={tool.to} to={tool.to} style={styles.toolRow}>
                <span style={styles.toolName}>{tool.label}</span>
                <span style={styles.toolDesc}>{tool.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

const TOOL_LINKS = [
  {
    to: "/template-families",
    label: "families",
    desc: "template family definitions",
  },
  { to: "/reviews", label: "reviews", desc: "review queue + sign-off" },
  { to: "/agent", label: "agent", desc: "agent suggestions" },
] as const;

const styles: Record<string, React.CSSProperties> = {
  content: { ...ui.pageContent, maxWidth: 680 },
  errorText: {
    color: "var(--comp-salmon)",
    fontSize: 13,
  },
  list: {},
  row: {
    display: "flex",
    alignItems: "flex-start",
    gap: "var(--space-3)",
    padding: "8px 0",
    borderBottom: "1px solid var(--comp-rule)",
  },
  dot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    marginTop: 6,
    flexShrink: 0,
  },
  rowBody: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  rowName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
  },
  rowDetail: {
    fontSize: 12,
    color: "var(--fg-2)",
    fontFamily: "var(--font-mono)",
  },
  repoLink: {
    color: "var(--fg-2)",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  rowHint: {
    fontSize: 11,
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    marginTop: 2,
  },
  rowError: {
    fontSize: 11,
    color: "var(--comp-salmon)",
    fontFamily: "var(--font-mono)",
  },
  toolsBlock: {
    marginTop: "var(--space-5)",
  },
  toolsList: {
    display: "flex",
    flexDirection: "column" as const,
  },
  toolRow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 2,
    padding: "8px 0",
    borderBottom: "1px solid var(--comp-rule)",
    textDecoration: "none",
  },
  toolName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
  },
  toolDesc: {
    fontSize: 12,
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
  },
};
